import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1608904872226-19d69391c761?q=85&w=2000";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name || "traveller"}`);
      navigate(user.role === "admin" ? "/admin" : "/portal", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row bg-background relative z-10">
      {/* Visual side */}
      <div className="relative hidden lg:block lg:w-1/2 overflow-hidden">
        <img src={HERO} alt="Luxury travel" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute inset-0 flex flex-col justify-between p-14">
          <img src="/logo.png" alt="Travel Mor" className="w-40 h-40 object-contain -ml-2" />
          <div>
            <h1 className="font-serif text-6xl font-light tracking-tighter text-white leading-none">
              Your journey,<br />
              <span className="gold-text">curated to perfection.</span>
            </h1>
            <p className="mt-6 text-muted-foreground max-w-md tracking-wide">
              Escape. Indulge. Remember. Access your bespoke itinerary and travel details in one elegant place.
            </p>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center px-6 py-16 lg:w-1/2">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/logo.png" alt="Travel Mor" className="w-28 h-28 object-contain" />
          </div>
          <p className="text-primary text-xs tracking-[0.3em] uppercase mb-3">Travel Mor</p>
          <h2 className="font-serif text-4xl font-light tracking-tighter mb-2">Sign in</h2>
          <p className="text-muted-foreground text-sm mb-10">
            Welcome back. Enter your details to continue.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs tracking-widest uppercase text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="bg-input border-border h-12 focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs tracking-widest uppercase text-muted-foreground">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-primary/80 hover:text-primary transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="bg-input border-border h-12 focus-visible:ring-primary"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-destructive-foreground bg-destructive/20 border border-destructive/40 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-12 rounded-full bg-primary text-primary-foreground font-medium tracking-wide hover:scale-[1.02] transition-transform gold-glow"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
